import React, { useEffect, useState } from 'react';
import { Typography, Row, Col, Card, Space, Button, Divider, Modal, Form, Input, Checkbox, message } from 'antd';
import { ArrowLeftOutlined, MailOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './PlatformPage.css';

const { Title, Text, Paragraph } = Typography;

const PlatformPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [pageLoadTime] = useState(Date.now());
  
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem('waitlist_session_id');
    if (existing) return existing;
    
    const generateId = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
    };
    
    const newId = generateId();
    localStorage.setItem('waitlist_session_id', newId);
    return newId;
  });

  // Set page metadata
  useEffect(() => {
    document.title = 'Platform | SNS Intelligence';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'SNS Market Intelligence Platform - A B2B pharmaceutical market intelligence and analytics platform providing structured data and analytical capabilities for strategic and commercial decision-making.');
  }, []);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async (values: any) => {
    if (isSubmitting) return;
    
    message.success('You\'re on the list. We\'ll reach out when your access is approved.');
    setIsSubmitted(true);
    form.resetFields();
    setIsSubmitting(true);
    
    try {
      const hiddenFields = {
        referrer_url: document.referrer || undefined,
        landing_path: window.location.pathname,
        session_id: sessionId,
        honeypot: '',
        submit_timing: Date.now() - pageLoadTime
      };

      const response = await fetch('https://pharmadashv2-backend.onrender.com/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          marketing_opt_in: values.marketing_opt_in,
          ...hiddenFields
        }),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      const result = await response.json();
      
      if (result.status === 'accepted' || result.status === 'ok') {
        // Success - already shown message
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Waitlist submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateCompanyEmail = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Please enter your email'));
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject(new Error('Please enter a valid email'));
    }
    
    // List of free email providers to reject
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
      'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'yandex.com',
      'gmx.com', 'zoho.com', 'msn.com', 'inbox.com', 'mail.ru'
    ];
    
    const emailDomain = value.toLowerCase().split('@')[1];
    
    if (freeEmailProviders.includes(emailDomain)) {
      return Promise.reject(new Error('Please use your company email address'));
    }
    
    return Promise.resolve();
  };

  return (
    <div className="platform-page">
      {/* Navigation */}
      <nav className="platform-navbar">
        <div className="nav-container">
          <div className="nav-left">
            <div
              className="nav-logo"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  navigate('/');
                }
              }}
            >
              <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
            </div>
            <div className="nav-links">
              <a href="/platform">Platform</a>
              <a href="/markets">Markets</a>
              <a href="/pulse">Pulse</a>
              <a href="/press">Press</a>
            </div>
          </div>
          <div className="nav-actions">
            <Button type="primary" onClick={() => navigate('/login')}>Open Portal</Button>
          </div>
        </div>
      </nav>

      {/* Back to Home Button */}
      <div className="back-to-home-section">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-to-home-button"
        >
          Back to Home
        </Button>
      </div>

      <div className="platform-container">
        <Row justify="center">
          <Col xs={24} md={20} lg={16}>
            <div className="platform-header">
              <Title level={1} className="platform-title">
                SNS Market Intelligence Platform
              </Title>
            </div>

            <Card className="platform-card">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Paragraph className="platform-intro">
                  SNS Intelligence has engineered a pharmaceutical market intelligence platform providing analytical capabilities and data across multiple levels of granularity to support strategic and commercial decision-making. The platform delivers market insights through consistent analytical frameworks, enabling professionals to evaluate opportunities, monitor competitive dynamics, and inform long-term planning.
                </Paragraph>

                <div>
                  <Title level={2} className="section-title">
                    Comprehensive Pharmaceutical Market Intelligence
                  </Title>
                  <Paragraph>
                    The platform provides market intelligence across multiple dimensions of the pharmaceutical industry. Users can access market sizing and forecasting data that supports revenue planning and opportunity assessment. Drug pipeline tracking capabilities enable monitoring of development activity across therapeutic areas and development stages.
                  </Paragraph>
                  <Paragraph>
                    Competitive and developer landscape analysis provides visibility into company activity, strategic positioning, and market participation. The platform covers multiple therapeutic areas and regions, offering a comprehensive view of pharmaceutical markets. All insights are derived from consistent analytical methodologies, ensuring reliability in decision support.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Core Data Domains
                  </Title>
                  <Paragraph>
                  The platform integrates data across several core domains essential for pharmaceutical market analysis. At the core of the platform is a sophisticated pipeline tracking engine that captures the full lifecycle of drug development. Pipelines are tracked with information on development stages, therapeutic classifications, and the intricate web of partnerships. Market size and forecast data provide quantitative assessments of market opportunities across time horizons and geographic regions.
                  </Paragraph>
                  <Paragraph>
                    Therapeutic and sub-market segmentation enables analysis at multiple levels of granularity, from broad therapeutic categories to specific sub-markets. Company and developer activity data supports competitive intelligence and partnership evaluation. Regional and country-level market data allows for geographic analysis and market prioritization. These domains are integrated to enable consistent analysis across regions, therapeutic areas, and time horizons.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Analytical Capabilities
                  </Title>
                  <Paragraph>
                    Users can directly compare markets across regions and time horizons, enabling evaluation of geographic opportunities and temporal trends. Pipeline activity can be directly compared by therapeutic area and development stage, supporting portfolio planning and competitive monitoring.
                  </Paragraph>
                  <Paragraph>
                    The platform supports analysis of competitive dynamics within specific markets, helping identify key players, market share trends, and strategic positioning. Sub-markets within broader therapeutic categories can be analyzed independently, enabling detailed opportunity assessment and market segmentation strategies.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Intended Users
                  </Title>
                  <Paragraph>
                    The platform is designed for professional users engaged in pharmaceutical market analysis and strategic planning. Market research and intelligence teams use the platform to support market assessments and competitive analysis. Business development and strategy teams leverage the data for opportunity evaluation and portfolio planning.
                  </Paragraph>
                  <Paragraph>
                    Pharmaceutical and biotechnology leadership utilize the platform for strategic decision-making and market prioritization. Consulting and advisory professionals access the platform to support client engagements and market analysis projects.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Use in Strategic and Commercial Decision-Making
                  </Title>
                  <Paragraph>
                    The platform supports market evaluation and opportunity assessment by providing quantitative market data and competitive context. Users can evaluate market size, growth trajectories, and competitive dynamics to inform opportunity prioritization.
                  </Paragraph>
                  <Paragraph>
                    Portfolio planning and prioritization are supported through pipeline analysis and market sizing data. Competitive monitoring capabilities enable tracking of developer activity, pipeline developments, and market participation trends. The platform facilitates long-term strategic analysis through historical data and forward-looking forecasts.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Access and Availability
                  </Title>
                  <Paragraph>
                    SNS Intelligence is a subscription-based platform with controlled access, intended for professional and corporate use. Access is managed through organizational subscriptions, ensuring appropriate use and data security.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Request Access to the Platform
                  </Title>
                  <div style={{ marginTop: '24px' }}>
                    <Space size="middle">
                      <Button 
                        type="primary" 
                        size="large"
                        onClick={showModal}
                      >
                        Request Free Trial
                      </Button>
                      <Button 
                        size="large"
                        onClick={() => {
                          navigate('/');
                          // Scroll to pricing section after navigation
                          setTimeout(() => {
                            const pricingSection = document.getElementById('pricing');
                            if (pricingSection) {
                              pricingSection.scrollIntoView({ behavior: 'smooth' });
                            } else {
                              // Fallback: try using hash
                              window.location.hash = 'pricing';
                            }
                          }, 300);
                        }}
                      >
                        View Pricing
                      </Button>
                    </Space>
                  </div>
                  <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e8e8e8' }}>
                    <Text style={{ color: '#595959', fontSize: '16px' }}>
                      Have questions? Contact us at{' '}
                      <a 
                        href="mailto:info@snsintel.com" 
                        style={{ 
                          color: '#1890ff', 
                          textDecoration: 'none',
                          fontWeight: 500
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                      >
                        info@snsintel.com
                      </a>
                    </Text>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Footer */}
      <footer className="platform-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>&copy; 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Trial Request Modal */}
      <Modal
        title="Request Free Trial"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={500}
      >
        {!isSubmitted ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Form.Item
              name="email"
              label="Company Email"
              rules={[{ validator: validateCompanyEmail }]}
            >
              <Input 
                placeholder="your.email@company.com" 
                size="large"
                prefix={<MailOutlined />}
              />
            </Form.Item>

            <Form.Item
              name="marketing_opt_in"
              valuePropName="checked"
              rules={[{ 
                validator: (_, value) => 
                  value ? Promise.resolve() : Promise.reject(new Error('Please accept to continue'))
              }]}
            >
              <Checkbox>
                I agree to receive updates about the trial and marketing communications.
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                size="large"
                loading={isSubmitting}
                block
                htmlType="submit"
              >
                Request Free Trial
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
            <Title level={4}>You're on the list!</Title>
            <Paragraph>
              We'll reach out when your access is approved.
            </Paragraph>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PlatformPage;
